import { Box, CircularProgress, Container, Typography } from "@mui/material";
import { useContext, useRef, useState } from "react";
import { MainContext } from "../../../main/context/MainContextProvider";
import { DashboardContext } from "../context/DashboardContexProvider";
import {
  EquipmentFilter,
  GetRopCardParams,
  ItemLayoutConfig,
  ItemLayoutsConfig,
} from "../types";
import DashboardCardHeader from "./DashboardCardHeader";
import { breakpoints } from "../../../main/themes/defaultLayoutData";
import {
  BaseErrorCard,
  BaseUsinaStringValue,
  PlantaDataDisponibilidade,
  PlantaDataEnergiaGerada,
  PlantaDataValorGerado,
  getSelectedArea,
} from "../models/dados-indicadores.model";
import { EmptyTable } from "../../clients/components/EmptyState";
import {
  formatNumber,
  formatNumberCurrencyBRL,
  formatNumberPercent,
} from "../helpers/format-number";

import {prRealizadoCardId, 
  performanceRatioCardId,
  energiaGeradaCardId,
  valorGeradoCardId,
  TensaoDeLinhaCardId,
  disponibilidadeCardId,
  potenciaAtivaCardId,
  energiaAcumuladaMWhCardId} from "../../../main/themes/defaultLayoutData";
  
import {MdOutlineAttachMoney} from "react-icons/md";
import { GoPulse } from "react-icons/go";
import { SlEnergy } from "react-icons/sl";
import { LuGauge } from "react-icons/lu";
import { FaBatteryEmpty} from "react-icons/fa";
import { IoMdCheckmarkCircleOutline, IoMdPower } from "react-icons/io";

export type TextCardContentProps = {
  cardId?: string;
  title?: string;
  value?: string;
  hourlyValues?: BaseUsinaStringValue[];
  cardError?: BaseErrorCard;
  planta?:
  | PlantaDataEnergiaGerada
  | PlantaDataValorGerado
  | PlantaDataDisponibilidade;
  lastUpdate: Date;
  cardType: number;
  secondaryData?: {
    title?: string;
    value: string;
  }[];
  backgroundStyle?: "positive" | "negative" | undefined;
};
type TextCardFontSizes = {
  titleFontSize: number;
  valueFontSize: number;
  secondaryDataFontSize: number;
};
function getDataGridByScreenSize(
  layoutsConfig: ItemLayoutsConfig,
  screenWidth: number,
  cardId?: string
): ItemLayoutConfig | undefined {
  if (screenWidth >= breakpoints.lg) {
    return layoutsConfig.layouts.lg?.find((layout) => {
      return layout.cardId === cardId;
    });
  }
  if (screenWidth >= breakpoints.md) {
    return layoutsConfig.layouts.md?.find((layout) => {
      return layout.cardId === cardId;
    });
  }
  if (screenWidth >= breakpoints.sm) {
    return layoutsConfig.layouts.sm?.find((layout) => {
      return layout.cardId === cardId;
    });
  }
  if (screenWidth >= breakpoints.xs) {
    return layoutsConfig.layouts.xs?.find((layout) => {
      return layout.cardId === cardId;
    });
  }
  if (screenWidth >= breakpoints.xxs) {
    return layoutsConfig.layouts.xxs?.find((layout) => {
      return layout.cardId === cardId;
    });
  }
}

function getFontSizeByCardAndScreenSize(
  layoutsConfig: ItemLayoutsConfig,
  screenWidth: number,
  cardId?: string
): TextCardFontSizes {
  const dataGrid = getDataGridByScreenSize(layoutsConfig, screenWidth, cardId);
  if (!dataGrid || !cardId) {
    return {
      titleFontSize: 12,
      valueFontSize: 24,
      secondaryDataFontSize: 14,
    };
  } else {
    const dataGridTitleFontSize = dataGrid
      ? dataGrid?.w < dataGrid?.h
        ? dataGrid?.w * 18
        : dataGrid?.h * 18
      : 18;
    const titleFontSize =
      dataGridTitleFontSize < 12 ? 18 : dataGridTitleFontSize;
    const dataGridValueFontSize = dataGrid
      ? dataGrid?.w < dataGrid?.h
        ? dataGrid?.w * 17
        : dataGrid?.h * 17
      : 24;
    const valueFontSize =
      dataGridValueFontSize < 24 ? 24 : dataGridValueFontSize;
    const dataGridSecondaryDataFontSize = dataGrid
      ? dataGrid?.w < dataGrid?.h
        ? dataGrid?.w * 6
        : dataGrid?.h * 6
      : 14;
    const secondaryDataFontSize =
      dataGridSecondaryDataFontSize < 14 ? 14 : dataGridSecondaryDataFontSize;
    return {
      titleFontSize,
      valueFontSize,
      secondaryDataFontSize,
    };
  }
}

function getValue(
  value: string | undefined,
  equipmentFilter: EquipmentFilter,
  isEnergiaGeradaCard: boolean,
  isValorGeradoCard: boolean,
  isDisponibilidadeCard: boolean,
  plantaData: any
) {
  if ((equipmentFilter.ugName || equipmentFilter.inversorName) && plantaData) {
    if (isDisponibilidadeCard) {
      return formatNumberPercent(plantaData.values.value.value / 100, 2);
    }
    if (isEnergiaGeradaCard) {
      return formatNumber(plantaData.values.gerada.value, 2, "MWh");
    }
    if (isValorGeradoCard) {
      return formatNumberCurrencyBRL(plantaData.values.gerado.value);
    }
  }
  return value;
}

function getSecundaryData(
  secundaryData:
    | {
      title?: string;
      value: string;
    }[]
    | undefined,
  equipmentFilter: EquipmentFilter,
  isEnergiaGeradaCard: boolean,
  isValorGeradoCard: boolean,
  plantaData: any
) {
    if (isValorGeradoCard) {
      return undefined
    }
  if ((equipmentFilter.ugName || equipmentFilter.inversorName) && plantaData) {
    if (isEnergiaGeradaCard) {
      return [
        {
          title: "Prevista",
          value: formatNumber(plantaData.values.prevista.value, 2),
        },
        {
          title: "% Prevista",
          value: formatNumberPercent(
            plantaData.values.previstaPercentual.value,
            2,
            true
          ),
        },
      ];
    }
  }
  return secundaryData;
}

function getHourlyValues(

  hourlyValues: BaseUsinaStringValue[] | undefined,
  equipmentFilter: EquipmentFilter,
  isDisponibilidadeCard: boolean,
  plantaData: any
): BaseUsinaStringValue[] {
  if ((equipmentFilter.ugName || equipmentFilter.inversorName) && plantaData) {
    if (isDisponibilidadeCard) {
      return plantaData.values.hourlyValues.map((item: any) => {
        return {
          ...item,
          value: formatNumberPercent(Number(item.value) ?? 0, 2),
        };
      });
    }
  }
  return hourlyValues ?? [];
}

function getCardIcon(
  cardId: string | undefined
): React.JSX.Element | undefined {
  if (!cardId) return undefined;

  if (cardId.includes(prRealizadoCardId.split("-")[0])) {
    return <GoPulse/>;
  } else if (cardId.includes(performanceRatioCardId.split("-")[0])) {
    return <LuGauge/>;
  } else if (cardId.includes(energiaGeradaCardId.split("-")[0])) {
    return <SlEnergy/>;
  }
  else if (cardId.includes(valorGeradoCardId.split("-")[0])) {
    return <MdOutlineAttachMoney/>;
  }
  else if (cardId.includes(TensaoDeLinhaCardId.split("-")[0])) {
    return <SlEnergy/>;
  }
  else if (cardId.includes(disponibilidadeCardId.split("-")[0])) {
    return <IoMdCheckmarkCircleOutline/>;
  } else if (cardId.includes(potenciaAtivaCardId.split("-")[0])) {
    return <IoMdPower/>;
  } else if (cardId.includes(energiaAcumuladaMWhCardId.split("-")[0])) {
    return <FaBatteryEmpty/>;
  }
  return undefined;
}

export function TextCardContent(props: TextCardContentProps) {
  const {
    title,
    value,
    secondaryData,
    lastUpdate,
    cardError,
    cardId,
    planta,
    cardType,
    backgroundStyle,
  } = props;
  const { theme } = useContext(MainContext);
  const {
    layoutsConfig: loyoutsConfig,
    equipmentFilter,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);
  const { titleFontSize, valueFontSize, secondaryDataFontSize } =
    getFontSizeByCardAndScreenSize(loyoutsConfig, screen.width, props.cardId);
  const [isMouseIn, setIsMouseIn] = useState(false);
  const timeout = 120;

  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;
  const elementRef = useRef(null);

  const plantaData = planta && getSelectedArea(planta, equipmentFilter);

  const isTensaoDeLinhaCard = cardId?.includes("TensaoDeLinha-");
  const isEnergiaGeradaCard = cardId?.includes("EnergiaGerada-") ?? false;
  const isValorGeradoCard = cardId?.includes("ValorGerado-") ?? false;
  const isDisponibilidadeCard = cardId?.includes("Disponibilidade-") ?? false;

  const displayableValue = getValue(
    value,
    equipmentFilter,
    isEnergiaGeradaCard,
    isValorGeradoCard,
    isDisponibilidadeCard,
    plantaData
  );
  const displayableSecundaryData = getSecundaryData(
    secondaryData,
    equipmentFilter,
    isEnergiaGeradaCard,
    isValorGeradoCard,
    plantaData
  );
  const displayableHourlyValues = getHourlyValues(
    props.hourlyValues,
    equipmentFilter,
    isDisponibilidadeCard,
    plantaData
  );

  const hasData = true;
  const [hourlyValue, setHourlyValue] = useState<string>();

    function getTextStyle(
      backgroundStyle: "positive" | "negative" | undefined
    ) {
      if (backgroundStyle === "positive") {
        return "green";
      }
      if (backgroundStyle === "negative") {
        return "red";
      }
    }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
      display={"flex"}
      flexDirection={"column"}
      onMouseOver={() => setIsMouseIn(true)}
      onMouseLeave={() => setIsMouseIn(false)}
    >
      <DashboardCardHeader
        icon={getCardIcon(cardId)}
        cardError={cardError}
        fullWidth
        hasDelay={hasDelay}
        lastUpdate={lastUpdate}
        cardId={props.cardId}
        elementRef={!isTensaoDeLinhaCard ? elementRef : undefined}
        isMouseIn={isMouseIn}
        title={title}
        titleFontSize={titleFontSize}
        hideExportImage
        cardType={props.cardType}
        hourlyValues={displayableHourlyValues}
        setHourlyValue={setHourlyValue}
        hasFilter
        hasEquipmentFilter={
          isEnergiaGeradaCard || isValorGeradoCard || isDisponibilidadeCard
        }
      />
      {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
        hasData ? (
          <Container
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
            }}
          >
            {(hourlyValue || displayableValue) && (
              <Typography
                className="pb-2"
                sx={{ fontSize: valueFontSize }}
                variant="h5"
              >
                {hourlyValue ?? displayableValue}
              </Typography>
            )}
            {displayableSecundaryData && (
              <Box
                alignItems={"center"}
                display={"flex"}
                flexDirection={"column"}
              >
                {displayableSecundaryData.map((data, index) => {
                  return (
                    <Box
                      display={"flex"}
                      justifyContent={data.title ? "space-between" : "center"}
                      key={index}
                      width={"70%"}
                      minWidth={"180px"}
                    >
                      {data.title && 
                      <Typography
                        sx={{ fontSize: secondaryDataFontSize }}
                        color={backgroundStyle ? getTextStyle(backgroundStyle) : theme.secondaryTextColor}
                        gutterBottom
                      >
                        {data.title}
                      </Typography>}
                      <Typography
                        sx={{ fontSize: secondaryDataFontSize }}
                        color={backgroundStyle ? getTextStyle(backgroundStyle) : theme.secondaryTextColor}
                        gutterBottom
                      >
                        {data.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Container>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <EmptyTable title="Nenhum dado encontrado!" />
          </Box>
        )
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}

export function getCardIsLoading(
  cardType: number,
  cardIsLoading: boolean,
  requestParamCards: string[]
): boolean {
  const parsedRequestParamsCards: GetRopCardParams[] = requestParamCards.map(
    (param) => JSON.parse(param)
  );

  if (parsedRequestParamsCards.find((card) => card.ropCardType === cardType)) {
    return cardIsLoading;
  }
  return false;
}
